import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { User } from '@doorly/shared';
import { NotProvisioned } from './NotProvisioned';
import { DoorlyLogo } from './DoorlyLogo';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8080';

type GateResult = { status: 'ok'; user: User } | { status: 'forbidden' } | { status: 'unauthorized' };

export function ProvisionGate({ children }: { children: ReactNode }) {
  const { getToken, isLoaded } = useAuth();

  const { data, isLoading } = useQuery<GateResult>({
    queryKey: ['me', 'gate'],
    queryFn: async (): Promise<GateResult> => {
      // Force a fresh token — Clerk's cache may hold an expired JWT
      const token = await getToken({ skipCache: true });
      if (!token) return { status: 'unauthorized' };
      const res = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) return { status: 'unauthorized' };
      if (res.status === 403) return { status: 'forbidden' };
      if (!res.ok) return { status: 'unauthorized' };
      const user = (await res.json()) as User;
      return { status: 'ok', user };
    },
    retry: false,
    staleTime: 60_000,
    enabled: isLoaded,
  });

  if (!isLoaded || isLoading || !data) {
    return (
      <div className="min-h-dvh grid place-items-center bg-bg">
        <DoorlyLogo variant="icon" size={80} className="animate-pulse" />
      </div>
    );
  }

  if (data.status === 'forbidden') return <NotProvisioned reason="not_provisioned" />;
  if (data.status === 'unauthorized') return <NotProvisioned reason="no_token" />;

  return <>{children}</>;
}
